import { Box, Breadcrumb, Card, Flex, Link as CardLink } from '@cowsquare/design';
import { Link } from 'gatsby';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React from 'react';
import { Query } from 'react-apollo';
import styled from 'styled-components';
import { BreadcrumbWrapper } from '../../../BreadcrumbWrapper';
import { Field, RowLink, RowTitle, generateRowValue } from '../../../MedicalContent';
import { Section } from '../../../Section';
import { Title } from '../../../Title';
import { HTMLContent } from '../../../HTMLContent';
import PageNotFound from '../../error/404';

const Logo = styled(Box).attrs({
  width: ['72px', '152px'],
  height: ['72px', '152px'],
})`
  background-image: url("${props => props.imgUrl}");
  background-position: center center;
  background-size: cover;
`;

const GET_DRUG_RECORD = gql`
  query getDrugRecord($id: uuid!) {
    record: hkdutest_drugs_by_pk(id: $id) {
      id
      name
      common_name
      chemical_name
      side_effects
      storage_requirement
      dosage_in_adult
      direction_for_use
      dosage_in_geriatric
      dosage_in_pediatric
      hk_permit_no
      indications
      interactions
      link_1
      link_2
      link_3
      mechanism_of_action
      official_bonus_terms
      precautions
      package_price
      package_description
      side_effects
      storage_requirement
      supplier_product_id
      class {
        name
        super_class {
          name
        }
      }
      manufacture {
        id
        name
      }
      file_1 {
        id
        name
        link
      }
      file_2 {
        id
        name
        link
      }
      file_3 {
        id
        name
        link
      }
      forensic_classifications {
        forensic_classification {
          name
        }
      }
      cover {
        link
      }
      active_ingredients
      brand {
        id
        name
      }
      distributor
    }
  }
`;

const FileCardList = ({ list }) => (
  <Box mx={[0, -4]} my={[-4, 0]} pt={[0, 5]}>
    <Flex flexWrap="wrap">
      {R.map(({ id, name, link }) => (
        <Box key={id} width={[1, 1 / 3]} px={[0, 4]} py={[4, 0]}>
          <Card height={80} href={link}>
            <Card.Meta title={name}>
              <CardLink.Group>
                <CardLink icon="ATTACHMENT" />
              </CardLink.Group>
            </Card.Meta>
          </Card>
        </Box>
      ))(list)}
    </Flex>
  </Box>
);

const DrugSearchContent = ({ id }) => {
  return (
    <Query query={GET_DRUG_RECORD} variables={{ id }}>
      {({ data, loading }) => {
        if (!loading && !R.prop('record', data)) {
          return <PageNotFound />;
        }

        const record = R.propOr({}, 'record', data);
        const photo = R.pathOr('', ['cover', 'link'], record);
        const links = R.pipe(R.pick(['link_1', 'link_2', 'link_3']), R.filter(R.identity), R.values)(record);
        const files = R.pipe(R.pick(['file_1', 'file_2', 'file_3']), R.filter(R.identity), R.values)(record);
        const classes = [
          R.path(['class', 'super_class', 'name'])(record),
          R.path(['class', 'name'])(record),
        ].join(' / ');
        const forensicClassifications = R.pipe(
          R.propOr([], 'forensic_classifications'),
          R.chain(R.pathOr({}, ['forensic_classification', 'name']))
        )(record);

        return (
          <Section>
            <BreadcrumbWrapper>
              <Breadcrumb>
                <Breadcrumb.Item linkProps={{ as: Link, to: '/e-pharm/drug-search' }}>
                  Drug Search
                </Breadcrumb.Item>
                <Breadcrumb.Item>{record.name}</Breadcrumb.Item>
              </Breadcrumb>
            </BreadcrumbWrapper>
            <Box mt={[7, 8]} mx={[5, 0]}>
              {photo && <Logo imgUrl={photo} />}
              <Title py={6}>{record.name}</Title>
              <Box pb={8}>
                <RowTitle>Basic Information</RowTitle>
                <Field
                  label="Brand Name / Trade Name"
                  value={record.brand}
                  render={({ id, name }) => (
                    <RowLink href={`/e-pharm/pharm-dir/${id}`} target="_blank">
                      {name}
                    </RowLink>
                  )}
                />
                <Field label="Chemical Name" value={record.chenical_name} />
                <Field label="Common Name" value={record.common_name} />
                <Field
                  label="Manufacturer"
                  value={record.manufacture}
                  render={({ id, name }) => (
                    <RowLink href={`/e-pharm/pharm-dir/${id}`} target="_blank">
                      {name}
                    </RowLink>
                  )}
                />
                <Field
                  label="Distributor"
                  value={record.distributor}
                  render={html => <HTMLContent html={html} />}
                />
                <Field
                  label="Links to other websites"
                  value={links}
                  render={generateRowValue([], link => (
                    <RowLink href={link} target="_blank">
                      {link}
                    </RowLink>
                  ))}
                />
                <Field
                  label="File for Download"
                  value={files}
                  render={files => <FileCardList list={files} />}
                  direction="column"
                />
                <Field
                  label="Active Ingredients"
                  value={record.active_ingredients}
                  render={html => <HTMLContent html={html} />}
                />
                <Field
                  label="Forensic Classification"
                  value={forensicClassifications}
                  render={R.join(', ')}
                />
                <Field label="Drug Classification" value={classes} />
              </Box>
              <Box pb={8}>
                <RowTitle>Ordering Information</RowTitle>
                <Field label="Package Price" value={record.package_price} render={price => `HK $${price}`} />
                <Field label="Official Bonus Terms" value={record.official_bonus_terms} />
                <Field label="HK Permit No." value={record.hk_permit_no} />
                <Field label="Supplier Product ID" value={record.supplier_product_id} />
              </Box>
              {record.indications && (
                <Box pb={8}>
                  <RowTitle>Indication / Uses</RowTitle>
                  <HTMLContent html={record.indications} />
                </Box>
              )}
              {record.dosage_in_adult && (
                <Box pb={8}>
                  <RowTitle>Dosage in Adults</RowTitle>
                  <HTMLContent html={record.dosage_in_adult} />
                </Box>
              )}
              {record.dosage_in_pediatric && (
                <Box pb={8}>
                  <RowTitle>Dosage in Pediatric</RowTitle>
                  <HTMLContent html={record.dosage_in_pediatric} />
                </Box>
              )}
              {record.dosage_in_geriatric && (
                <Box pb={8}>
                  <RowTitle>Dosage in Geriatric</RowTitle>
                  <HTMLContent html={record.dosage_in_geriatric} />
                </Box>
              )}
              {record.direction_for_use && (
                <Box pb={8}>
                  <RowTitle>Direction for Use</RowTitle>
                  <HTMLContent html={record.direction_for_use} />
                </Box>
              )}
              {record.precautions && (
                <Box pb={8}>
                  <RowTitle>Precautions</RowTitle>
                  <HTMLContent html={record.precautions} />
                </Box>
              )}
              {record.side_effects && (
                <Box pb={8}>
                  <RowTitle>Side Effects / Adverse Reactions</RowTitle>
                  <HTMLContent html={record.side_effects} />
                </Box>
              )}
              {record.mechanism_of_action && (
                <Box pb={8}>
                  <RowTitle>Mechanism of Action</RowTitle>
                  <HTMLContent html={record.mechanism_of_action} />
                </Box>
              )}
              {record.interactions && (
                <Box pb={8}>
                  <RowTitle>Interactions</RowTitle>
                  <HTMLContent html={record.interactions} />
                </Box>
              )}
              {record.storage_requirement && (
                <Box pb={8}>
                  <RowTitle>Storage Requirement</RowTitle>
                  <HTMLContent html={record.storage_requirement} />
                </Box>
              )}
            </Box>
          </Section>
        );
      }}
    </Query>
  );
};

export default DrugSearchContent;
